import {
  Box,
  Button,
  type ButtonProps,
  Group,
  Stack,
  openConfirmModal,
  useShowNotification,
} from '@inflearn/ds-react';
import type { EditorFeature } from '@inflearn/editor';
import { useIsMutating } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useRef } from 'react';

import type { NoteWithUnitsNoteDetailResponse } from '../../../../../resources/@types/uccSchema';
import { getUseNoteIdUpdateMutationQueryKey } from '../../../../mutations/useNoteIdUpdateMutation';
import Editor from '../../../common/Editor';

type Props = {
  onCancel: () => void;
  onSubmit: (content: string) => Promise<unknown>;
  disabled?: boolean;
  initialValue?: string;
  buttonProps?: {
    cancel?: ButtonProps;
    submit?: ButtonProps;
  };
  noteId: NoteWithUnitsNoteDetailResponse['id'];
};

export default function NoteEditor({
  onCancel,
  onSubmit,
  disabled = false,
  initialValue = '',
  buttonProps,
  noteId,
}: Props) {
  const { t } = useTranslation();
  const editor = useRef<EditorFeature>(null);

  const { showNotification } = useShowNotification();

  const isMutating = useIsMutating({
    mutationKey: getUseNoteIdUpdateMutationQueryKey(String(noteId)),
  });

  const handleCancel = () => {
    openConfirmModal({
      size: 'xs',
      footerType: 'right',
      withCloseButton: true,
      title: t('작성을 그만둘까요?'),
      children: t('그만둘 경우 작성 중인 내용이 모두 사라져요.'),
      labels: {
        cancel: t('취소'),
        confirm: t('그만두기'),
      },
      confirmProps: {
        color: 'red',
      },
      onConfirm: onCancel,
    });
  };

  const handleSubmit = async () => {
    if (!editor.current) {
      return;
    }

    const editorContent = await editor.current.getSubmitContent();
    const isEmptyContent = isEmpty(editorContent.trim());

    if (isEmptyContent) {
      showNotification({
        title: t('내용을 입력해주세요.'),
        type: 'error',
      });

      return;
    }

    return onSubmit(editorContent);
  };

  return (
    <Stack spacing={8} w="100%" miw={0}>
      <Box mih={200}>
        <Editor
          ref={editor}
          disabled={disabled}
          initialValue={initialValue}
          autoFocus
          height="auto"
          minHeight="200px"
          maxHeight="480px"
        />
      </Box>
      <Group spacing={8} position="right">
        <Button
          size="sm"
          radius="md"
          color="gray"
          variant="subtle"
          {...buttonProps?.cancel}
          onClick={handleCancel}
        >
          {t('취소')}
        </Button>
        <Button
          size="sm"
          radius="md"
          color="infgreen"
          variant="filled"
          {...buttonProps?.submit}
          onClick={handleSubmit}
          loading={isMutating > 0}
        >
          {t('저장')}
        </Button>
      </Group>
    </Stack>
  );
}
